import { useCallback, useMemo, useState } from 'react'
import { createContainer } from 'unstated-next'

import { reportClientError } from '../../../app/_components/chrome/scripts/DataDogRumScript'
import { useTranslation } from '../../../app/_providers/TranslationProvider.client'
import { shortenShopifyIdToNumeric } from '../../../app/_utils/shortenShopifyId'
import { generateLineItemProperties } from '../../../containers/EmbroideryService/utils'
import { CartContainer } from '../cart'

import type { Open, QuickBuyCartItem, QuickBuyData, State } from './types'
import type { EmbroideryConfig, EmbroideryVariant, Variant } from '../../../types/graphql'

function useQuickBuy({
  defaultGarmentProperties,
  defaultEmbroideryProperties,
}: {
  /** Additional line item properties that should be included on garments added through quick buy */
  defaultGarmentProperties?: Record<string, string>
  /** Additional line item properties that should be included on embroidery items added through quick buy */
  defaultEmbroideryProperties?: Record<string, string>
} = {}) {
  const { addItems } = CartContainer.useContainer()

  const {
    cart: { finalSale, nonReturnable },
  } = useTranslation()

  const finalSaleProperty = useMemo(() => {
    return { [finalSale]: nonReturnable }
  }, [finalSale, nonReturnable])

  const [state, setState] = useState<State>({ modalState: 'Unopened' })

  const openModal = useCallback(
    (data: QuickBuyData) => {
      setState({ modalState: 'Open', ...data })
    },
    [setState]
  )

  const closeModal = useCallback(() => {
    setState(current => ({ ...(current as Open), modalState: 'Closed' }))
  }, [setState])

  const addToCart = useCallback(
    ({
      embroideryConfig,
      embroideryVariant,
      finalSale,
      variant,
      swPromoEligible,
      quantity,
      openMiniCartOnAdd,
    }: {
      embroideryConfig?: EmbroideryConfig
      embroideryVariant?: EmbroideryVariant
      finalSale?: boolean
      variant: Variant
      swPromoEligible?: boolean
      quantity?: number
      openMiniCartOnAdd?: boolean
    }) => {
      const getItems = () => {
        const item: QuickBuyCartItem = {
          finalSale,
          item: variant,
          swPromoEligible,
          properties: defaultGarmentProperties ?? {},
        }

        if (finalSale) item.properties = { ...item.properties, ...finalSaleProperty }

        if (!embroideryVariant)
          return [
            {
              ...item,
              quantity,
              openMiniCartOnAdd,
            },
          ]

        const shopifyId = shortenShopifyIdToNumeric(variant.shopifyId)

        if (!shopifyId) return [item]

        // Line item properties for the embroidery item
        const embroideryProperties = {
          ...defaultEmbroideryProperties,
          ...generateLineItemProperties(
            {
              embroideryItemVariant: { ...embroideryVariant, images: [] },
              logoName: embroideryConfig?.logoName,
              logoImageSource: embroideryConfig?.image,
            },
            undefined,
            shopifyId
          ),
        }

        // Line item properties for the product
        const garmentProperties = {
          ...item.properties,
          ...finalSaleProperty,
          _embroideryId: variant.shopifyId,
          _guid: embroideryProperties._guid,
          _LPROP: embroideryProperties._LPROP,
        }

        return [
          { item: embroideryVariant, properties: embroideryProperties, isPortalLogo: true },
          {
            ...item,
            properties: garmentProperties,
            isPortalColor: true,
          },
        ]
      }

      try {
        addItems(getItems())
      } catch (error) {
        reportClientError({
          error,
          context: {
            scope: 'QuickBuy Add to Bag',
          },
        })

        throw error
      }
    },
    [addItems, finalSaleProperty, defaultGarmentProperties, defaultEmbroideryProperties]
  )

  return useMemo(
    () => ({
      addToCart,
      closeModal,
      openModal,
      state,
    }),
    [addToCart, closeModal, openModal, state]
  )
}
/**
 * @deprecated Use QuickbuyDialog.client.tsx from app directory which is based on newer apis.
 */
export const QuickBuyContainer = createContainer(useQuickBuy)
